import { useEffect, useState } from 'react';
import { fetchMission, fetchMissionServers, type ServerRating } from '../api/missions';
import { fetchPlayableServers, type PlayableServer } from '../api/servers';
import { fetchTerrains, type TerrainView } from '../api/catalog';
import { fetchSessions } from '../api/sessions';
import { ca } from '../i18n/ca';
import { fitOf, usable, withRating, type MissionMod, type ServerFit } from './serverFit';

/** A server and how well it fits one mission (serverFit.ts). */
export interface RatedServer {
  server: PlayableServer;
  fit: ServerFit;
}

/**
 * Why a server cannot run this mission now, or null when it can. Every
 * server the panel lists stays in the list, the group's own unlinked ones
 * greyed with the way to link them (the platform refuses a session on those,
 * 409 server_not_linked); among the rest the picker refuses nothing it does
 * not have to.
 */
export function blockedReason(
  server: PlayableServer,
  fit: ServerFit,
  busyServers: Set<string>,
): string | null {
  if (!usable(server)) return ca.missionUi.page.serverNotLinked;
  if (busyServers.has(server.name)) return ca.missionUi.page.serverBusy;
  if (fit.fit === 'no') return ca.missionUi.page.serverNoTerrain;
  return null;
}

/**
 * Every server the panel lists, rated against one mission, and the ones a live
 * session already holds. Nothing is fetched until `enabled`, so a reader who
 * may not direct the session costs the panel nothing.
 */
export function useRatedServers(
  missionCode: string | null,
  enabled: boolean,
): { rated: RatedServer[]; busyServers: Set<string> } {
  const [servers, setServers] = useState<PlayableServer[]>([]);
  const [terrains, setTerrains] = useState<TerrainView[]>([]);
  const [ratings, setRatings] = useState<ServerRating[]>([]);
  const [busyServers, setBusyServers] = useState<Set<string>>(new Set());
  const [mission, setMission] = useState<{
    worldName: string;
    modSetHash: string | null;
    /** The list the mission was made with: what a server is rated against. */
    mods: MissionMod[];
  } | null>(null);

  useEffect(() => {
    if (!enabled || !missionCode) return;
    let current = true;
    fetchMission(missionCode)
      .then((m) => {
        if (current) {
          setMission({
            worldName: m.definition.terrain.worldName,
            modSetHash: m.modSetHash ?? null,
            mods: m.definition.modSet?.mods ?? [],
          });
        }
      })
      .catch(() => current && setMission(null));
    fetchPlayableServers()
      .then((r) => current && setServers(r.servers))
      .catch(() => current && setServers([]));
    fetchTerrains()
      .then((t) => current && setTerrains(t))
      .catch(() => current && setTerrains([]));
    // Class by class, where the platform can say; the mod lists otherwise.
    fetchMissionServers(missionCode)
      .then((r) => current && setRatings(r))
      .catch(() => current && setRatings([]));
    fetchSessions(true)
      .then(
        (r) =>
          current &&
          setBusyServers(
            new Set(
              r.rows
                .filter((row) => row.state === 'LIVE' && row.serverName)
                .map((row) => row.serverName as string),
            ),
          ),
      )
      .catch(() => current && setBusyServers(new Set()));
    return () => {
      current = false;
    };
  }, [enabled, missionCode]);

  if (!mission) return { rated: [], busyServers };
  const terrainModId =
    terrains.find((t) => t.worldName.toLowerCase() === mission.worldName.toLowerCase())
      ?.modWorkshopId ?? null;
  return {
    rated: servers.map((s) => ({
      server: s,
      fit: withRating(
        fitOf(s, mission.mods, terrainModId, mission.modSetHash),
        ratings.find((r) => r.serverId === s.id),
      ),
    })),
    busyServers,
  };
}
